import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import type { DismissalItem, LibraryItem } from "../lib/api.js";
import { BookmarkButton } from "./BookmarkButton.js";
import { DismissButton } from "./DismissButton.js";
import { linkForItem } from "../lib/linkForItem.js";

/**
 * Horizontal-scrolling row of cards, used by the Home page to render each
 * recommendation section. Wheel scrolls horizontally; the left/right buttons
 * page by ~80%.
 */
export function MediaStrip({
  label,
  items,
  variant = "default",
  caption,
  onRefresh,
  href,
  episodeLevel,
  onDismiss,
  dismissing = false,
}: {
  label: string;
  items: (LibraryItem & {
    last_position_s?: number;
    duration_s?: number | null;
    tmdb_rating?: number | null;
    tmdb_genres?: string[] | null;
  })[];
  variant?: "default" | "wide" | "poster";
  caption?: string;
  onRefresh?: () => void;
  /** Resume contexts (Continue Watching): series cards link to the specific episode, not the show page. */
  episodeLevel?: boolean;
  /** When set, the strip label links to the service's drill-in browse page. */
  href?: string;
  onDismiss?: (item: DismissalItem) => void;
  dismissing?: boolean;
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  function scrollBy(dir: -1 | 1): void {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-3">
          {href ? (
            <Link to={href} className="section-label flex items-center gap-1 hover:text-gold transition-colors" title={`Browse all · ${label}`}>
              {label} <ChevronRight size={12} />
            </Link>
          ) : (
            <div className="section-label">{label}</div>
          )}
          {caption && <div className="text-parchment/60 text-xs">{caption}</div>}
        </div>
        <div className="flex gap-1">
          <button className="pill-ghost p-2" onClick={() => scrollBy(-1)} aria-label="scroll left">
            <ChevronLeft size={16} />
          </button>
          <button className="pill-ghost p-2" onClick={() => scrollBy(1)} aria-label="scroll right">
            <ChevronRight size={16} />
          </button>
          {onRefresh && (
            <button className="pill-ghost p-2 text-parchment/50 hover:text-gold" onClick={onRefresh} aria-label="refresh">
              <RefreshCw size={14} />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((it) => (
          <StripCard
            key={it.id}
            item={it}
            variant={variant}
            episodeLevel={episodeLevel}
            onDismiss={onDismiss}
            dismissing={dismissing}
          />
        ))}
      </div>
    </section>
  );
}

function StripCard({
  item,
  variant,
  episodeLevel,
  onDismiss,
  dismissing = false,
}: {
  item: LibraryItem & {
    last_position_s?: number;
    duration_s?: number | null;
    tmdb_rating?: number | null;
    tmdb_genres?: string[] | null;
    episode_count?: number;
  };
  variant: "default" | "wide" | "poster";
  episodeLevel?: boolean;
  onDismiss?: (item: DismissalItem) => void;
  dismissing?: boolean;
}): JSX.Element {
  const width =
    variant === "wide" ? "min-w-[320px]" :
    variant === "poster" ? "min-w-[180px]" :
    "min-w-[220px]";
  const aspectClass = variant === "poster" ? "aspect-[2/3]" : "aspect-video";
  const rating = typeof item.tmdb_rating === "number" ? Math.round(item.tmdb_rating * 10) / 10 : null;
  const hasProgress =
    typeof item.last_position_s === "number" &&
    item.last_position_s > 0 &&
    typeof item.duration_s === "number" &&
    item.duration_s > 0;
  const pct = hasProgress
    ? Math.min(100, Math.round((item.last_position_s! / item.duration_s!) * 100))
    : null;
  const watchedFmt = hasProgress ? formatDuration(Math.floor(item.last_position_s!)) : null;
  const leftFmt = hasProgress
    ? formatDuration(Math.max(0, Math.floor(item.duration_s! - item.last_position_s!)))
    : null;
  return (
    <div className={`${width} snap-start relative group`}>
      <Link
        to={linkForItem({ id: item.id, kind: item.kind, category: item.category }, { episodeLevel })}
        className="card group block overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi hover:scale-[1.04] transition-transform duration-200 ease-spring"
      >
      <div className={`${aspectClass} bg-elev relative`}>
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-2xl">
            {item.title.slice(0, 2).toUpperCase()}
          </div>
        )}
        <BookmarkButton itemId={item.id} />
        <SourceBadge kind={item.kind} hasDismiss={!!onDismiss} />
        {rating !== null && (
          <span
            className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/85 border border-gold/40 font-display tabular-nums text-goldHi"
            title={`TMDB rating ${rating}/10`}
          >
            ★ {rating}
          </span>
        )}
        {/* Bottom-right badge: total runtime if not started, "left" if in progress */}
        {hasProgress ? (
          <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/85 text-goldHi tabular-nums font-display">
            {leftFmt} left
          </span>
        ) : item.duration_s !== null && (item.duration_s ?? 0) > 0 ? (
          <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/80 text-ivory tabular-nums">
            {formatDuration(item.duration_s!)}
          </span>
        ) : null}
        {pct !== null && pct > 0 && (
          <div className="absolute left-0 right-0 bottom-0 h-1 bg-ink/70">
            <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">{item.title}</div>
        <div className="text-parchment text-xs mt-1 truncate">
          {item.kind === "tv_show" && item.episode_count
            ? `${item.episode_count} episode${item.episode_count === 1 ? "" : "s"}`
            : (("creator_name" in item && item.creator_name) ? item.creator_name : (item.category ?? ""))}
        </div>
        {hasProgress && (
          <div className="text-goldHi/80 text-xs mt-1 tabular-nums font-display tracking-smallcaps">
            {watchedFmt} watched · {pct}%
          </div>
        )}
      </div>
      </Link>
      {onDismiss && (
        <DismissButton itemTitle={item.title} onDismiss={() => onDismiss(item)} disabled={dismissing} />
      )}
    </div>
  );
}

function SourceBadge({ kind, hasDismiss }: { kind: string; hasDismiss: boolean }): JSX.Element {
  const { label, color } = badgeFor(kind);
  return (
    <span
      className={`absolute top-2 ${hasDismiss ? "right-11" : "right-2"} px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display`}
      style={{ background: "rgba(15,6,6,0.78)", color, border: `1px solid ${color}55` }}
    >
      {label}
    </span>
  );
}

function badgeFor(kind: string): { label: string; color: string } {
  switch (kind) {
    case "youtube_video": return { label: "YouTube", color: "#E8C977" };
    case "live_channel":  return { label: "Live",    color: "#2C6E5B" };
    case "vod":           return { label: "Movie",   color: "#C7A24A" };
    case "tv_show":       return { label: "TV",      color: "#C42D3A" };
    default:              return { label: kind,      color: "#A89A7C" };
  }
}

function formatDuration(s: number): string {
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}
